import { format, parse } from 'date-fns'
import { useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useSubcategories } from '~/entities/categories'
import {
  getDocumentStepUrl,
  getDocumentsUrlWithCurrentParams,
  getIsPaymentDepositFinished,
  useAuditTrailEvents,
  useCurrentDocument,
  usePaymentSummary,
  useUpdateDocumentPayor,
  useUpdatePaymentSummary,
} from '~/entities/document'
import {
  FLAG_TYPES,
  FlagCommentsPopover,
  getActiveFlagByType,
  getLatestResolvedFlagByType,
  useAddFlagComment,
  useCreateDocumentFlag,
  useDocumentFlags,
  useResolveDocumentFlag,
  useStepFlagState,
} from '~/entities/document-flags'
import { SuggestedPayorField } from '~/entities/payors'
import { useAuditTrailQueryParams } from '~/features/audit-trail'
import { useFirstInteraction } from '~/features/document-analytics'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { useAppForm } from '~/shared/lib/form'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import {
  Button,
  Checkbox,
  Chip,
  DatePicker,
  LocalIcon,
  RadioButton,
  Segment,
  SegmentedButton,
  Text,
  Textfield,
} from '~/shared/ui'
import { ReviewActions } from '~/widgets/review-actions'
import { CommentsPopover } from './comments-popover'

type PaymentType = apiClient.PaymentSummaryUpdateRequest['payment_type']

interface PaymentDepositFormValues {
  paymentType: PaymentType
  payorId: string
  totalPaid: number | null
  checkDate: string
  depositDate: string
  checkNumber: string
  depositReceived: boolean | null
  achTraceNumber: string
  bankReferenceNumber: string
  epcDraftNumber: string
}

function buildDefaultValues(
  summaryData: apiClient.PaymentSummaryResponse | undefined,
  documentPayorId: string | null | undefined,
): PaymentDepositFormValues {
  return {
    paymentType: summaryData?.payment_type ?? 'check',
    payorId: documentPayorId ?? '',
    totalPaid: summaryData?.total_paid ? Number(summaryData?.total_paid) : null,
    checkDate: summaryData?.check_date ?? '',
    depositDate: summaryData?.deposit_date ?? '',
    checkNumber: summaryData?.check_number ?? '',
    depositReceived: summaryData?.receipt_confirmed ?? null,
    achTraceNumber: summaryData?.ach_trace_number ?? '',
    bankReferenceNumber: summaryData?.bank_reference_number ?? '',
    epcDraftNumber: summaryData?.epc_draft_number ?? '',
  }
}

/** Builds the API request body from form values. */
function buildRequestBody(
  values: PaymentDepositFormValues,
  options?: { notes?: string | null },
): apiClient.PaymentSummaryUpdateRequest {
  if (values.paymentType === 'credit_card') {
    return {
      payment_type: values.paymentType,
      total_paid: values.totalPaid,
      check_date: null,
      deposit_date: null,
      check_number: null,
      ach_trace_number: null,
      bank_reference_number: null,
      epc_draft_number: values.epcDraftNumber || null,
      receipt_confirmed: false,
      ...(options?.notes !== undefined && { notes: options.notes }),
    }
  }
  return {
    payment_type: values.paymentType,
    total_paid: values.totalPaid,
    check_date: values.checkDate,
    deposit_date: values.depositDate,
    check_number: values.paymentType === 'check' ? values.checkNumber : null,
    ach_trace_number: values.paymentType === 'direct_deposit' ? values.achTraceNumber : null,
    bank_reference_number: values.bankReferenceNumber || null,
    receipt_confirmed: values.depositReceived,
    ...(options?.notes !== undefined && { notes: options.notes }),
  }
}

export function PaymentDepositContent() {
  const formRef = useRef<HTMLDivElement>(null)
  useFirstInteraction(formRef)
  const navigate = useNavigate()
  const documentKey = useParams<'documentKey'>().documentKey as string
  const { data: summaryData } = usePaymentSummary()
  const { mutateAsync: updatePaymentSummary, isPending: isSummaryUpdatePending } =
    useUpdatePaymentSummary()
  const { mutateAsync: updateDocumentPayor, isPending: isPayorUpdatePending } =
    useUpdateDocumentPayor()
  const { mutateAsync: createDocumentFlag, isPending: isFlagCreatePending } =
    useCreateDocumentFlag()
  const { mutateAsync: resolveDocumentFlag, isPending: isFlagResolvePending } =
    useResolveDocumentFlag()
  const { mutateAsync: addFlagComment, isPending: isFlagCommentPending } = useAddFlagComment()
  const {
    data: { document },
  } = useCurrentDocument()
  const subcategories = useSubcategories()
  const { data: flagsData } = useDocumentFlags()

  // Credit card flag view state derivation
  const activeCreditCardFlag = getActiveFlagByType(flagsData.items, FLAG_TYPES.CREDIT_CARD)
  const latestResolvedFlag = getLatestResolvedFlagByType(flagsData.items, FLAG_TYPES.CREDIT_CARD)
  const creditCardViewState: 'pending' | 'followUp' | 'reviewed' = activeCreditCardFlag
    ? 'followUp'
    : latestResolvedFlag
      ? 'reviewed'
      : 'pending'

  const [creditCardConfirmed, setCreditCardConfirmed] = useState(creditCardViewState === 'reviewed')
  const [commentText, setCommentText] = useState('')
  const creditCardFlag = activeCreditCardFlag ?? latestResolvedFlag ?? null

  const stepFlag = useStepFlagState(FLAG_TYPES.STEP_EOB_PAYMENT_DEPOSIT)

  // Filter audit trail for payment deposit comments (events with notes)
  const { data: auditTrailData } = useAuditTrailEvents({ documentKey })
  const comments = useMemo(
    () =>
      (auditTrailData?.events ?? [])
        .filter((e) => e.event_type === 'eob_payment_deposit' && e.notes)
        .sort((a, b) => a.timestamp - b.timestamp),
    [auditTrailData],
  )
  const hasComments = comments.length > 0
  const queryParams = useAuditTrailQueryParams()
  const { isScrolled: isScrolledBottom, sentinelElement: sentinelElementBottom } = useIsScrolled()

  const form = useAppForm({
    defaultValues: buildDefaultValues(summaryData, document.payor_id),
    validators: {
      onChange: ({ value }) => {
        const errors: Record<string, string> = {}
        if (value.paymentType !== 'credit_card') {
          if (value.totalPaid == null) errors.totalPaid = 'Required'
          if (value.paymentType === 'check' && !value.checkNumber) errors.checkNumber = 'Required'
          if (value.paymentType === 'direct_deposit' && !value.achTraceNumber)
            errors.achTraceNumber = 'Required'
          if (!value.bankReferenceNumber) errors.bankReferenceNumber = 'Required'
          if (value.depositReceived == null) errors.depositReceived = 'Required'
          if (value.depositReceived === true && !value.depositDate) errors.depositDate = 'Required'
        }
        return Object.keys(errors).length ? { fields: errors } : undefined
      },
      onMount: ({ value }) => {
        const errors: Record<string, string> = {}
        if (value.paymentType !== 'credit_card') {
          if (value.totalPaid == null) errors.totalPaid = 'Required'
          if (value.paymentType === 'check' && !value.checkNumber) errors.checkNumber = 'Required'
          if (value.paymentType === 'direct_deposit' && !value.achTraceNumber)
            errors.achTraceNumber = 'Required'
          if (!value.bankReferenceNumber) errors.bankReferenceNumber = 'Required'
          if (value.depositReceived == null) errors.depositReceived = 'Required'
          if (value.depositReceived === true && !value.depositDate) errors.depositDate = 'Required'
        }
        return Object.keys(errors).length ? { fields: errors } : undefined
      },
    },
  })

  /** Best-effort save before navigating back to the work queue. */
  async function handleReturnToWorkQueue() {
    try {
      await updatePaymentSummary({
        path: { document_key: documentKey },
        body: buildRequestBody(form.state.values, { notes: commentText || null }),
      })
    } catch {
      // Best-effort — don't block navigation on failure.
    }

    setCommentText('')
    navigate(getDocumentsUrlWithCurrentParams())
  }

  /** Create flag, save payment summary, update payor if changed, then navigate to work queue. */
  async function handleSaveAndReturnToWork() {
    const values = form.state.values

    // Create flag first — abort if it fails to avoid partial state
    try {
      await createDocumentFlag({
        path: { key: documentKey },
        body: { flag_type: FLAG_TYPES.CREDIT_CARD, document_step: 'eob_payment_deposit' },
      })
    } catch {
      return
    }

    const body = buildRequestBody(values)
    try {
      await updatePaymentSummary({
        path: { document_key: documentKey },
        body: { ...body, status: null },
      })
    } catch {
      // Best-effort — flag already created, continue to save payor and navigate
    }

    if (values.payorId !== (document.payor_id ?? '')) {
      try {
        await updateDocumentPayor({
          path: { key: document.key },
          body: { payor_id: values.payorId },
        })
      } catch {
        // Best-effort
      }
    }

    navigate(getDocumentsUrlWithCurrentParams())
  }

  /** Resolve credit card flag (if still active), save payment summary, update payor, then navigate. */
  async function handleValidate() {
    const values = form.state.values

    if (activeCreditCardFlag) {
      try {
        await resolveDocumentFlag({
          path: { key: documentKey, flagKey: activeCreditCardFlag.key },
          body: {},
        })
      } catch {
        return
      }
    }

    const body = buildRequestBody(values)
    try {
      await updatePaymentSummary({
        path: { document_key: documentKey },
        body: { ...body, status: null, receipt_confirmed: true },
      })
    } catch {
      // Best-effort — flag already resolved, continue
    }

    if (values.payorId !== (document.payor_id ?? '')) {
      try {
        await updateDocumentPayor({
          path: { key: document.key },
          body: { payor_id: values.payorId },
        })
      } catch {
        // Best-effort
      }
    }

    navigate(getDocumentStepUrl(documentKey, 'eob_duplication', { queryParams }))
  }

  /** Save payment summary, update payor if changed, then navigate to duplication step. */
  async function handleContinue() {
    const values = form.state.values

    const body = buildRequestBody(values)
    try {
      await updatePaymentSummary({
        path: { document_key: documentKey },
        body: { ...body, status: null },
      })
    } catch {
      // On failure the user stays on this step — no action needed.
      return
    }

    // Update payor if it changed
    if (values.payorId !== (document.payor_id ?? '')) {
      try {
        await updateDocumentPayor({
          path: { key: document.key },
          body: { payor_id: values.payorId },
        })
      } catch {
        return
      }
    }

    navigate(getDocumentStepUrl(documentKey, 'eob_duplication', { queryParams }))
  }

  return (
    <div ref={formRef}>
      <div className="px-6 py-4">
        <div className="flex flex-col gap-2">
          <div className="flex min-h-7 items-center gap-5">
            <Text appearance="SUBTITLE2">Confirm Payment Deposit</Text>
            <form.Subscribe selector={(state) => state.values.paymentType}>
              {(paymentType) =>
                paymentType === 'credit_card' && (
                  <div className="flex items-center gap-2">
                    {creditCardViewState === 'followUp' && (
                      <Chip
                        size="sm"
                        attention
                        rounded
                      >
                        Follow Up
                      </Chip>
                    )}
                    {creditCardViewState === 'reviewed' && (
                      <Chip
                        size="sm"
                        success
                        rounded
                      >
                        Reviewed
                      </Chip>
                    )}
                    {creditCardViewState === 'pending' && (
                      <Chip
                        size="sm"
                        attention
                        rounded
                      >
                        Pending review
                      </Chip>
                    )}
                  </div>
                )
              }
            </form.Subscribe>
          </div>
          <Text
            appearance="CAPTION"
            className="text-text-secondary"
          >
            Select payment type and provide required details.
          </Text>
        </div>

        <div className="mt-6 flex">
          <form.Field name="paymentType">
            {(field) => (
              <SegmentedButton size="small">
                <Segment
                  checked={field.state.value === 'check'}
                  onClick={() => {
                    field.handleChange('check')
                    form.setFieldValue('depositReceived', null)
                  }}
                >
                  Check
                </Segment>
                <Segment
                  checked={field.state.value === 'direct_deposit'}
                  onClick={() => {
                    field.handleChange('direct_deposit')
                    form.setFieldValue('depositReceived', null)
                  }}
                >
                  Direct Deposit
                </Segment>
                <Segment
                  checked={field.state.value === 'credit_card'}
                  onClick={() => {
                    field.handleChange('credit_card')
                    form.setFieldValue('depositReceived', null)
                  }}
                >
                  Credit Card
                </Segment>
              </SegmentedButton>
            )}
          </form.Field>
        </div>

        <form.Subscribe selector={(state) => state.values.paymentType}>
          {(paymentType) => (
            <>
              {paymentType === 'check' && (
                <>
                  <div className="mt-4 flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-4">
                      <form.Field name="payorId">
                        {(field) => (
                          <SuggestedPayorField
                            label="Payor Name"
                            value={field.state.value}
                            onChange={(value) => field.handleChange(value)}
                            onBlur={field.handleBlur}
                          />
                        )}
                      </form.Field>
                      <form.Field name="totalPaid">
                        {(field) => (
                          <Textfield
                            label="Total Paid"
                            currency
                            value={field.state.value ?? undefined}
                            onCentsChange={(cents) => field.handleChange(cents)}
                            required
                          />
                        )}
                      </form.Field>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      <form.Field name="checkDate">
                        {(field) => (
                          <DatePicker
                            label="Check Date"
                            date={
                              field.state.value
                                ? parse(field.state.value, 'yyyy-MM-dd', new Date())
                                : undefined
                            }
                            onDateChange={(date) => {
                              const value = date ? format(date, 'yyyy-MM-dd') : ''
                              field.handleChange(value)
                            }}
                          />
                        )}
                      </form.Field>
                      <form.Subscribe selector={(state) => state.values.depositReceived}>
                        {(depositReceived) => (
                          <form.Field name="depositDate">
                            {(field) => (
                              <DatePicker
                                label="Deposit Date"
                                required={depositReceived === true}
                                date={
                                  field.state.value
                                    ? parse(field.state.value, 'yyyy-MM-dd', new Date())
                                    : undefined
                                }
                                onDateChange={(date) => {
                                  const value = date ? format(date, 'yyyy-MM-dd') : ''
                                  field.handleChange(value)
                                }}
                              />
                            )}
                          </form.Field>
                        )}
                      </form.Subscribe>
                      <form.Field name="checkNumber">
                        {(field) => (
                          <Textfield
                            label="Check Number"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            required
                          />
                        )}
                      </form.Field>
                      <form.Field name="bankReferenceNumber">
                        {(field) => (
                          <Textfield
                            label="Bank Reference #"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            required
                            leadingIcon={
                              <LocalIcon
                                className="text-content-high-emphasis block"
                                name="ia"
                              />
                            }
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>

                  <div className="mt-8">
                    <Text appearance="SUBTITLE2">Confirm Payment Deposit</Text>
                    <div className="mt-3 grid grid-cols-3 gap-4">
                      <form.Field name="depositReceived">
                        {(field) => {
                          const isNotReceived = field.state.value === false
                          const showIcon = isNotReceived || hasComments
                          const isReadOnly = !isNotReceived
                          return (
                            <>
                              <button
                                className="bg-surface-lowest border-input-outlined-enabled-border flex cursor-pointer items-center rounded-sm border px-4"
                                type="button"
                                onClick={() => field.handleChange(true)}
                              >
                                <RadioButton
                                  label="Received"
                                  checked={field.state.value === true}
                                  onChange={() => field.handleChange(true)}
                                  tabIndex={-1}
                                />
                              </button>
                              <button
                                className="bg-surface-lowest border-input-outlined-enabled-border flex flex-1 cursor-pointer items-center rounded-sm border px-4"
                                type="button"
                                onClick={() => field.handleChange(false)}
                              >
                                <RadioButton
                                  label="Not received"
                                  checked={field.state.value === false}
                                  onChange={() => field.handleChange(false)}
                                  tabIndex={-1}
                                />
                              </button>
                              <div className="relative">
                                <CommentsPopover
                                  visible={showIcon}
                                  comments={comments}
                                  isReadOnly={isReadOnly}
                                  commentText={commentText}
                                  onCommentChange={setCommentText}
                                />
                              </div>
                            </>
                          )
                        }}
                      </form.Field>
                    </div>
                  </div>
                </>
              )}

              {paymentType === 'direct_deposit' && (
                <>
                  <div className="mt-4 flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-4">
                      <form.Field name="payorId">
                        {(field) => (
                          <SuggestedPayorField
                            label="Payor Name"
                            value={field.state.value}
                            onChange={(value) => field.handleChange(value)}
                            onBlur={field.handleBlur}
                          />
                        )}
                      </form.Field>
                      <form.Field name="totalPaid">
                        {(field) => (
                          <Textfield
                            label="Total Paid"
                            currency
                            value={field.state.value ?? undefined}
                            onCentsChange={(cents) => field.handleChange(cents)}
                            required
                          />
                        )}
                      </form.Field>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <form.Subscribe selector={(state) => state.values.depositReceived}>
                        {(depositReceived) => (
                          <form.Field name="depositDate">
                            {(field) => (
                              <DatePicker
                                label="Deposit Date"
                                required={depositReceived === true}
                                date={
                                  field.state.value
                                    ? parse(field.state.value, 'yyyy-MM-dd', new Date())
                                    : undefined
                                }
                                onDateChange={(date) => {
                                  const value = date ? format(date, 'yyyy-MM-dd') : ''
                                  field.handleChange(value)
                                }}
                              />
                            )}
                          </form.Field>
                        )}
                      </form.Subscribe>
                      <form.Field name="achTraceNumber">
                        {(field) => (
                          <Textfield
                            label="ACH / EFT Number"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            required
                          />
                        )}
                      </form.Field>
                      <form.Field name="bankReferenceNumber">
                        {(field) => (
                          <Textfield
                            label="Bank Reference #"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            required
                            leadingIcon={
                              <LocalIcon
                                className="text-content-high-emphasis block"
                                name="ia"
                              />
                            }
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>

                  <div className="mt-8">
                    <Text appearance="SUBTITLE2">Confirm Payment Deposit</Text>
                    <div className="mt-3 grid grid-cols-3 gap-4">
                      <form.Field name="depositReceived">
                        {(field) => {
                          const isNotReceived = field.state.value === false
                          const showIcon = isNotReceived || hasComments
                          const isReadOnly = !isNotReceived
                          return (
                            <>
                              <button
                                className="bg-surface-lowest border-input-outlined-enabled-border flex cursor-pointer items-center rounded-sm border px-4"
                                type="button"
                                onClick={() => field.handleChange(true)}
                              >
                                <RadioButton
                                  label="Received"
                                  name="deposit-received"
                                  checked={field.state.value === true}
                                  onChange={() => field.handleChange(true)}
                                  tabIndex={-1}
                                />
                              </button>
                              <div className="flex items-center gap-2">
                                <button
                                  className="bg-surface-lowest border-input-outlined-enabled-border flex flex-1 cursor-pointer items-center rounded-sm border px-4"
                                  type="button"
                                  onClick={() => field.handleChange(false)}
                                >
                                  <RadioButton
                                    label="Not received"
                                    name="deposit-received"
                                    checked={field.state.value === false}
                                    onChange={() => field.handleChange(false)}
                                    tabIndex={-1}
                                  />
                                </button>
                                <CommentsPopover
                                  visible={showIcon}
                                  comments={comments}
                                  isReadOnly={isReadOnly}
                                  commentText={commentText}
                                  onCommentChange={setCommentText}
                                />
                              </div>
                            </>
                          )
                        }}
                      </form.Field>
                    </div>
                  </div>
                </>
              )}

              {paymentType === 'credit_card' && (
                <div className="mt-4 flex flex-col">
                  <div className="grid grid-cols-3 gap-4">
                    <form.Field name="payorId">
                      {(field) => (
                        <SuggestedPayorField
                          label="Payor Name"
                          value={field.state.value}
                          onChange={(value) => field.handleChange(value)}
                          onBlur={field.handleBlur}
                        />
                      )}
                    </form.Field>
                    <form.Field name="totalPaid">
                      {(field) => (
                        <Textfield
                          label="Total Paid"
                          currency
                          value={field.state.value ?? undefined}
                          onCentsChange={(cents) => field.handleChange(cents)}
                        />
                      )}
                    </form.Field>
                    <form.Field name="epcDraftNumber">
                      {(field) => (
                        <Textfield
                          label="EPC Draft Number"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                      )}
                    </form.Field>
                  </div>

                  {(creditCardViewState === 'followUp' || creditCardViewState === 'reviewed') && (
                    <div className="mt-6 flex items-start justify-between gap-4">
                      <Checkbox
                        label={
                          <div className="flex flex-col gap-1">
                            <Text appearance="CAPTION">
                              Confirm credit card enrollment is removed
                            </Text>
                            <Text
                              appearance="CAPTION"
                              className="text-text-tertiary"
                            >
                              Check once the credit card enrollment has been removed
                            </Text>
                          </div>
                        }
                        checked={creditCardConfirmed}
                        onChange={() => setCreditCardConfirmed((prev) => !prev)}
                      />
                      {creditCardFlag && (
                        <FlagCommentsPopover
                          mode="persisted"
                          flag={creditCardFlag}
                          canEdit={creditCardViewState === 'followUp'}
                          busy={isFlagCommentPending}
                          trigger={
                            <Button
                              size="small"
                              materialIcon={{
                                children: 'flag',
                                fill: creditCardViewState === 'followUp',
                              }}
                            >
                              Comments
                            </Button>
                          }
                          onAddComment={async (text) => {
                            await addFlagComment({
                              path: { key: documentKey, flagKey: creditCardFlag.key },
                              body: { notes: text },
                            })
                          }}
                          onResolve={async (text) => {
                            if (!activeCreditCardFlag) return
                            try {
                              if (text.trim()) {
                                await addFlagComment({
                                  path: {
                                    key: documentKey,
                                    flagKey: activeCreditCardFlag.key,
                                  },
                                  body: { notes: text },
                                })
                              }
                              await resolveDocumentFlag({
                                path: {
                                  key: documentKey,
                                  flagKey: activeCreditCardFlag.key,
                                },
                                body: {},
                              })
                            } catch {
                              // Surface via query error state; no inline error UI here.
                            }
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </form.Subscribe>
      </div>

      <form.Subscribe
        selector={(state) => ({
          isValid: state.isValid,
          isDefaultValue: state.isDefaultValue,
          depositReceived: state.values.depositReceived,
          paymentType: state.values.paymentType,
        })}
      >
        {({ isValid, isDefaultValue, depositReceived, paymentType }) => {
          const isCreditCard = paymentType === 'credit_card'
          const isNotReceived = depositReceived === false
          const isFollowUp = isCreditCard && creditCardViewState === 'followUp'
          const isReviewed = isCreditCard && creditCardViewState === 'reviewed'

          let onSubmit: () => void
          let submitLabel: string
          let submitDisabled: boolean
          let submitTooltip: string | undefined

          if (stepFlag.hasActiveStepFlag) {
            // Step-level flag forces "Return to Work Queue" — no save, no unsaved-changes prompt.
            // Coexists with credit-card flag: stays on this branch until step flag is resolved.
            onSubmit = () => navigate(getDocumentsUrlWithCurrentParams())
            submitLabel = 'Return to Work Queue'
            submitDisabled = false
            submitTooltip = undefined
          } else if (isFollowUp) {
            onSubmit = handleValidate
            submitLabel = 'Validate'
            submitDisabled =
              !creditCardConfirmed ||
              isSummaryUpdatePending ||
              isPayorUpdatePending ||
              isFlagCreatePending ||
              isFlagResolvePending
            submitTooltip = !creditCardConfirmed ? 'Resolve the flag to proceed' : undefined
          } else if (isReviewed && creditCardConfirmed) {
            onSubmit = handleSaveAndReturnToWork // not reachable — button disabled
            submitLabel = 'Validate'
            submitDisabled = true
            submitTooltip = undefined
          } else if (isReviewed && !creditCardConfirmed) {
            onSubmit = handleSaveAndReturnToWork
            submitLabel = 'Save & return to work'
            submitDisabled =
              isSummaryUpdatePending ||
              isPayorUpdatePending ||
              isFlagCreatePending ||
              isFlagResolvePending
            submitTooltip = undefined
          } else if (isCreditCard) {
            onSubmit = handleSaveAndReturnToWork
            submitLabel = 'Save & Return to Work'
            submitDisabled =
              isSummaryUpdatePending ||
              isPayorUpdatePending ||
              isFlagCreatePending ||
              isFlagResolvePending
            submitTooltip = undefined
          } else if (isNotReceived) {
            onSubmit = handleReturnToWorkQueue
            submitLabel = 'Return to Work Queue'
            submitDisabled =
              (getIsPaymentDepositFinished(document, subcategories) && isDefaultValue) ||
              isSummaryUpdatePending ||
              isPayorUpdatePending
            submitTooltip = undefined
          } else {
            onSubmit = handleContinue
            submitLabel = 'Validate'
            submitDisabled =
              !isValid ||
              (getIsPaymentDepositFinished(document, subcategories) && isDefaultValue) ||
              isSummaryUpdatePending ||
              isPayorUpdatePending
            submitTooltip = !isValid ? 'Fill in all required fields to validate.' : undefined
          }

          return (
            <ReviewActions
              isDirty={false}
              isSubmitDisabled={submitDisabled}
              submitTooltip={submitTooltip}
              onSubmit={onSubmit}
              submitLabel={submitLabel}
              isScrolled={isScrolledBottom}
              previousStepUrl={getDocumentStepUrl(document.key, 'classification', { queryParams })}
              previousStepLabel="Go to Classification"
            />
          )
        }}
      </form.Subscribe>

      {sentinelElementBottom}
    </div>
  )
}
